import { SCHEDULED_CHAT_KEY } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	isStoreAssistant,
	isStoreHuman,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { getScheduleRovoChatRRuleFromExecutionMessage } from './getScheduleRovoChatRRuleFromExecutionMessage';
import { getScheduleRovoChatRuleIdUuidFromExecutionMessage } from './getScheduleRovoChatRuleIdUuidFromExecutionMessage';

type RuleMetadata = { ruleIdUuid: string; rRule?: string };

const isScheduledChat = (action: MessageAction): boolean => action.key === SCHEDULED_CHAT_KEY;

/** Replays persisted creation results onto the matching card after a history fetch. */
export const restoreScheduledChatRuleMetadata = (messages: StoreChatItem[]): StoreChatItem[] => {
	const scheduledInvocations = new Set<string>();
	const invocationsByHumanMessage = new Map<string, Set<string>>();

	let currentHumanMessageId: string | undefined;
	for (const item of messages) {
		if (isStoreHuman(item)) {
			currentHumanMessageId = item.content.id;
		}
		if (!isStoreAssistant(item)) continue;
		for (const action of item.content.message.message.actions ?? []) {
			if (!isScheduledChat(action)) continue;
			scheduledInvocations.add(action.invocationId);
			const humanMessageId =
				action.humanMessageId ?? item.content.message.human_message?.id ?? currentHumanMessageId;
			if (humanMessageId) {
				const invocations = invocationsByHumanMessage.get(humanMessageId) ?? new Set<string>();
				invocations.add(action.invocationId);
				invocationsByHumanMessage.set(humanMessageId, invocations);
			}
		}
	}

	const ruleMetadataByInvocation = new Map<string, RuleMetadata>();
	currentHumanMessageId = undefined;
	for (const item of messages) {
		if (isStoreHuman(item)) {
			currentHumanMessageId = item.content.id;
		}
		if (!isStoreAssistant(item)) continue;
		const message = item.content.message.message;
		const metadata = message.message_metadata;
		const ruleIdUuid = getScheduleRovoChatRuleIdUuidFromExecutionMessage(message);
		if (
			!ruleIdUuid ||
			(metadata?.action_execution_result && metadata.action_execution_result !== 'SUCCESS')
		)
			continue;

		let invocationId = metadata?.invocation_id?.trim();
		if (!invocationId) {
			const ownInvocations = (message.actions ?? [])
				.filter(isScheduledChat)
				.map((action) => action.invocationId);
			const humanMessageId = item.content.message.human_message?.id ?? currentHumanMessageId;
			const candidates =
				ownInvocations.length > 0
					? new Set(ownInvocations)
					: invocationsByHumanMessage.get(humanMessageId ?? '');
			if (candidates?.size === 1) invocationId = candidates.values().next().value;
		}
		if (!invocationId || !scheduledInvocations.has(invocationId)) continue;
		const rRule = getScheduleRovoChatRRuleFromExecutionMessage(message);
		ruleMetadataByInvocation.set(invocationId, {
			ruleIdUuid,
			...(rRule ? { rRule } : {}),
		});
	}

	if (ruleMetadataByInvocation.size === 0) return messages;
	return messages.map((item) => {
		if (!isStoreAssistant(item)) return item;
		const message = item.content.message.message;
		let changed = false;
		const actions = message.actions?.map((action) => {
			const metadata = ruleMetadataByInvocation.get(action.invocationId);
			const data = action.data;
			if (
				!isScheduledChat(action) ||
				!metadata ||
				!data ||
				typeof data !== 'object' ||
				Array.isArray(data) ||
				action.status === 'cancelled' ||
				action.status === 'errored'
			)
				return action;
			const originalData = data as Record<string, unknown>;
			if (originalData.ruleIdUuid && originalData.ruleIdUuid !== metadata.ruleIdUuid) return action;
			changed = true;
			return {
				...action,
				status: 'finished' as const,
				data: {
					...originalData,
					ruleIdUuid: metadata.ruleIdUuid,
					...(metadata.rRule && !originalData.rRule ? { rRule: metadata.rRule } : {}),
				},
			};
		});
		return changed
			? {
					...item,
					content: {
						...item.content,
						message: { ...item.content.message, message: { ...message, actions } },
					},
				}
			: item;
	});
};
